import {
	type ForecastHorizons,
	type InvestmentSummary,
	type MarketScenario,
	type MarketScenarioMinInfo,
	type MarketViewAssetClassAliases,
	type PerformanceContributionEntry,
	ScenarioAnalysisControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import {
	type OrderBy,
	type TableColumn,
	AutoSortHScrollTable,
	CircularProgressBar,
	defaultRowHeight,
	ProgressBar,
	Row,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { overrideClassList, useDebouncedMemo, useSize } from "@mdotm/shadcn/legacy/react-extensions";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { ForEach } from "@mdotm/utils/react-extra";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApiGen } from "$root/api/hooks";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { DataDisplayOverlay } from "$root/components/DataDisplayOverlay";
import { IconWalls } from "$root/components/IconWall";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import type { MarketViewScenario } from "$root/components/Portfolio/MarketView";
import { MarketViewProbabilities } from "$root/functional-areas/market-view/MarketViewProbabilities";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { type IUser, useUserValue } from "$root/functional-areas/user";
import { useLocaleFormatters } from "$root/localization/hooks";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { useMappedQuery, useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { commonColumns } from "$root/ui-lib/common-table-columns";
import { type ContextContent, withContext } from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import {
	applyDeltaToSelectedMarketView,
	removeDeltaFromSelectedMarketView,
	round2Dec,
	roundCustom,
} from "../../../components/Portfolio/MarketView/utilsV2";
import { BarGraph } from "./BarGraph";

type portfolioPerformanceColumnProps = {
	metrics: string;
	portfolio?: number;
	benchmark?: number;
	graph: string;
};

type portfolioPerformanceContributionColumnProps = {
	portfolioReturns: number;
	portfolioExposure: number;
	benchmarkReturns: number;
	assetClass: string;
};

type ScenarioCategoryProps = {
	value: string;
	label: string;
	group: "Sphere forecasts" | "My views" | "Historical scenarios" | "";
	scenarioType: MarketViewScenario;
	forecastHorizon?: ForecastHorizons;
	enhancable: boolean;
	custom?: boolean;
};

const EXPECTED_PTF_PERFORMANCE_LEGENDS = [
	{ name: "Portfolio", color: "#00AEEF" },
	{ name: "Benchmark", color: "#D1D2DC" },
];
const EXPECTED_PTF_PERFORMANCE_CONTRIBUTION_LEGENDS = [
	{ name: "Portfolio returns", color: "#00AEEF" },
	{ name: "Benchmark returns", color: "#D1D2DC" },
];

/**
 * @internal — exported for tests
 *
 * Splits the flat scenario list into the three groups the selector shows (standard / custom /
 * historical), relabels the sphere / current-portfolio rows, hides sphere views for users that
 * lack the service, and returns them concatenated with custom sorted by value and historical sorted
 * by the year embedded in the label.
 */
export function buildScenarioOptions(
	scenarioAnalysisList: MarketScenarioMinInfo[] | undefined,
	deps: {
		formatDate: (date?: string) => string;
		marketScenarioFrom: string | undefined;
		user: IUser;
	},
): ScenarioCategoryProps[] {
	const { formatDate, marketScenarioFrom, user } = deps;
	if (!scenarioAnalysisList) {
		return [];
	}
	const composeAcc = (
		value: string,
		label: string,
		group: ScenarioCategoryProps["group"],
		scenarioType: ScenarioCategoryProps["scenarioType"],
		enhancable: boolean,
		forecastHorizon?: ForecastHorizons,
		custom?: boolean,
	) => ({
		value,
		label,
		group,
		enhancable,
		forecastHorizon,
		scenarioType,
		custom,
	});

	const { custom, historical, standard } = scenarioAnalysisList.reduce(
		(acc, el) => {
			const { historical: historicalAcc, standard: standardAcc, custom: customAcc } = acc;
			const { id, label, userCustomScenario, forecastHorizon } = el;

			if ((id ?? "").includes("CURRENT_PORTFOLIO") || (id ?? "").includes("SPHERE_FORECAST")) {
				const newLabel = id === "CURRENT_PORTFOLIO" ? `Market view ${formatDate(marketScenarioFrom)}` : (label ?? "-");
				const currPtf = composeAcc(
					id ?? "-",
					newLabel,
					"",
					id === "CURRENT_PORTFOLIO" ? "custom" : "sphere",
					(id ?? "").includes("SPHERE_FORECAST"),
					forecastHorizon,
				);
				return {
					historical: historicalAcc,
					custom: customAcc,
					standard: [...standardAcc, currPtf],
				};
			}

			if (userCustomScenario) {
				return {
					standard: standardAcc,
					historical: historicalAcc,
					custom: [
						...customAcc,
						composeAcc(id ?? "-", label ?? "-", "My views", "custom", true, forecastHorizon, el.customMarketView),
					],
				};
			}

			return {
				standard: hasAccess(user, {
					requiredService: "HIDE_SCENARIO_ANALYSIS_SPHERE_VIEWS",
				})
					? []
					: standardAcc,
				custom: customAcc,
				historical: [
					...historicalAcc,
					composeAcc(id ?? "-", label ?? "-", "Historical scenarios", "historical", false, forecastHorizon),
				],
			};
		},
		{
			historical: [] as Array<ScenarioCategoryProps>,
			standard: [] as Array<ScenarioCategoryProps>,
			custom: [] as Array<ScenarioCategoryProps>,
		},
	);

	const sortHistorycalRegimes = (a: ScenarioCategoryProps, b: ScenarioCategoryProps) => {
		const rgxHelper = (s: string) => {
			const rgx = new RegExp(/\d{4}/);
			const matches = rgx.exec(s);
			return matches ? matches[0] : "";
		};

		const valueA = rgxHelper(a.label);
		const valueB = rgxHelper(b.label);

		if (valueA > valueB) {
			return 1;
		}
		if (valueA < valueB) {
			return -1;
		}
		return 0;
	};

	return standard.concat(custom.sort(builtInSortFnFor("value"))).concat(historical.sort(sortHistorycalRegimes));
}

/**
 * @internal — exported for tests
 *
 * The set of asset-class / geography tickers that carry a positive portfolio exposure — used to
 * highlight the portfolio's own rows in the market-view editor. De-duplicated, order-preserving.
 */
export function deriveHighlightedAssetClass(performanceContributions: PerformanceContributionEntry[]): string[] {
	const filteredAC = performanceContributions.filter((el) => (el.portfolioExposure ?? 0) > 0);
	const selectedAC = filteredAC.reduce((acc, curr) => {
		const newAC =
			curr.marketViewAssetClassLabel && curr.marketViewAssetClassLabel.length > 0
				? curr.marketViewAssetClassLabel.map((el) => el.tickerName!)
				: [];
		if (curr.geographicalLink) {
			newAC.push(curr.geographicalLink);
		}
		return [...acc, ...newAC];
	}, [] as string[]);
	return [...new Set(selectedAC)];
}

/**
 * @internal — exported for tests
 *
 * Symmetric, rounded-up bounds for a bar graph: the max of the supplied magnitudes rounded up to
 * the next whole unit, mirrored to a negative minimum.
 */
export function symmetricRoundedLimits(absMagnitudes: number[]): {
	max: number;
	min: number;
} {
	const MAX = roundCustom(Math.max(...absMagnitudes));
	return {
		max: MAX,
		min: MAX * -1,
	};
}

/**
 * @internal — exported for tests
 *
 * Projects the default scenario onto a market-view with per-asset-class deltas applied from the
 * user regime probabilities. The three regime weights are derived cumulatively from
 * `regimeUserProbability` (a, a+b, and the remainder up to 100).
 */
export function mapScenarioWithDelta(marketScenario: MarketScenario): MarketScenario {
	const regimes = {
		lower: marketScenario.regimeUserProbability?.a ?? 0,
		upper: (marketScenario.regimeUserProbability?.a ?? 0) + (marketScenario.regimeUserProbability?.b ?? 0),
	};
	return {
		...marketScenario,
		flexibleExpectedReturnsVolatility: {
			assetClasses: applyDeltaToSelectedMarketView(
				{
					a: regimes.lower,
					b: regimes.upper - regimes.lower,
					c: 100 - regimes.upper,
				},
				marketScenario.flexibleExpectedReturnsVolatility?.assetClasses,
			),
		},
	} satisfies MarketScenario;
}

const ScenarioAnalysisEditableContentMemo = memo<{
	sphereScenario: MarketScenario;
	summary: InvestmentSummary;
	benchmarkId: string | null;
	performanceContributions: PerformanceContributionEntry[];
	enhanced: boolean;
	defaultScenarioId: string;
	marketViewAssetClassAliases: MarketViewAssetClassAliases | undefined;
	scenarioAnalysisList: MarketScenarioMinInfo[];
}>(function ScenarioAnalysisEditableContent({
	sphereScenario,
	summary,
	performanceContributions,
	enhanced,
	defaultScenarioId,
	marketViewAssetClassAliases,
	scenarioAnalysisList,
}) {
	const [size, setSize] = useState<HTMLDivElement | null>(null);
	const portfolioContributionPerformanceRowSize = useSize(size)?.width;
	const scenarioApi = useApiGen(ScenarioAnalysisControllerApiFactory);

	const showContributionGraph = useMemo(() => {
		if (!portfolioContributionPerformanceRowSize) {
			return true;
		}
		const percentOfGraphWidth = 20;
		const graphColumnWidth = (portfolioContributionPerformanceRowSize * percentOfGraphWidth) / 100;
		return graphColumnWidth > 110;
	}, [portfolioContributionPerformanceRowSize]);

	const { uuid } = summary ?? {};
	const { t } = useTranslation();
	const { formatNumber, formatDate } = useLocaleFormatters();
	const user = useUserValue();

	const [currentScenarioId, setCurrentScenarioId] = useState<string | null>(defaultScenarioId);
	const [originalScenario, setOriginalScenario] = useState<MarketScenario>(sphereScenario);
	const [marketScenario, setMarketScenario] = useState<MarketScenario>(sphereScenario);

	const selectedScenario = useMemo(
		() => scenarioAnalysisList?.find((x) => x.id === currentScenarioId),
		[currentScenarioId, scenarioAnalysisList],
	);

	const { value: queryKey } = useDebouncedMemo(
		() => [selectedScenario, marketScenario],
		[selectedScenario, marketScenario],
		{ debounceInterval: 300 },
	);

	const performanceQuery = useQueryNoRefetch(["queryMarkseScenarioEditablePerformance", queryKey], {
		keepPreviousData: true,
		enabled: selectedScenario != null,
		queryFn: () => {
			const { flexibleExpectedReturnsVolatility } = removeDeltaFromSelectedMarketView(
				marketScenario.flexibleExpectedReturnsVolatility!.assetClasses!,
				originalScenario.flexibleExpectedReturnsVolatility!.assetClasses!,
				marketScenario.regimeUserProbability!,
			);

			return axiosExtract(
				scenarioApi.getExpectedPerformance({
					enhancement: enhanced,
					expectedReturnsWithDelta: {
						assetClasses: flexibleExpectedReturnsVolatility,
					},
					investmentUuid: uuid,
					scenarioId: selectedScenario!.id,
				}),
			);
		},
	});

	const { data: performance } = performanceQuery;

	const portfolioContributionPerformanceRow = useMemo<Array<{
		portfolioExposure: number;
		portfolioReturns: number;
		benchmarkReturns: number;
		portfolioVolatility: number;
		benchmarkVolatility: number;
		assetClass: string;
	}> | null>(() => {
		if (selectedScenario == null) {
			return null;
		}
		return (
			performance?.expectedPerformanceAssetClassEntries?.map((x) => ({
				portfolioExposure: round2Dec(x.portfolioExposure!),
				portfolioReturns: round2Dec(x.portfolioReturns!),
				benchmarkReturns: round2Dec(x.benchmarkReturns!),
				portfolioVolatility: round2Dec(x.portfolioVolatility!),
				benchmarkVolatility: round2Dec(x.benchmarkVolatility!),
				assetClass: `${x.macroAssetClass} ${x.macroGeography} ${x.microAssetClass}`,
			})) ?? null
		);
	}, [performance?.expectedPerformanceAssetClassEntries, selectedScenario]);

	const scenarioOptions = useMemo(
		() =>
			buildScenarioOptions(scenarioAnalysisList, {
				formatDate,
				marketScenarioFrom: marketScenario?.historicalInvestmentHorizon?.from,
				user,
			}),
		[formatDate, marketScenario?.historicalInvestmentHorizon?.from, scenarioAnalysisList, user],
	);

	// Totals PTF Performance
	const portfolioPerformanceRow = useMemo(() => {
		if (!performance) {
			return null;
		}
		return [
			{
				benchmark: performance.benchmarkTotalAnnualizedReturns,
				graph: "",
				metrics: "Annualised returns",
				portfolio: performance.totalAnnualizedReturns,
			},
			{
				benchmark: performance.benchmarkTotalAnnualizedVolatility,
				graph: "",
				metrics: "Annualised volatility",
				portfolio: performance.totalAnnualizedVolatility,
			},
		];
	}, [performance]);

	const derivedContributionGraphLimits = useMemo(() => {
		if (!portfolioContributionPerformanceRow || !portfolioPerformanceRow) {
			return null;
		}
		const portfolioAndBenchmarkCReturns = portfolioContributionPerformanceRow
			.map((el) => Math.abs(el.benchmarkReturns))
			.concat(portfolioContributionPerformanceRow.map((el) => Math.abs(el.portfolioReturns)));
		return symmetricRoundedLimits(portfolioAndBenchmarkCReturns);
	}, [portfolioContributionPerformanceRow, portfolioPerformanceRow]);

	const derivedPerformanceGraphLimits = useMemo(() => {
		if (!portfolioPerformanceRow) {
			return null;
		}
		const portfolioAndBenchmarkVolatilityReturns = portfolioPerformanceRow.map((el) => [
			Math.abs(el.benchmark ?? NaN),
			Math.abs(el.portfolio ?? NaN),
		]);
		return symmetricRoundedLimits(portfolioAndBenchmarkVolatilityReturns.flat());
	}, [portfolioPerformanceRow]);

	// Highlight data that is related to the portfolio
	const highlightedAssetClass = useMemo(
		() => deriveHighlightedAssetClass(performanceContributions),
		[performanceContributions],
	);

	// TableColumns
	const portfolioPerformanceColumn = useMemo<TableColumn<portfolioPerformanceColumnProps>[]>(
		() => [
			{
				name: "metric",
				header: t("SCENARIO_ANALYSIS_EDITABLE.TABLES_FIELDS.METRIC"),
				content: ({ metrics }) => metrics,
				minWidth: 264,
				footer: (footerProps) => (
					<Row {...footerProps} gap={16} className="py-4">
						{EXPECTED_PTF_PERFORMANCE_LEGENDS.map(({ name, color }) => (
							<div key={color} className="flex items-center gap-1">
								<ColoredRectangle variant="vertical" color={color} />
								{name}
							</div>
						))}
					</Row>
				),
			},
			{
				...commonColumns.percentage("portfolio"),
				header: t("SCENARIO_ANALYSIS_EDITABLE.TABLES_FIELDS.PORTFOLIO"),
				width: 144,
			},
			{
				...commonColumns.percentage("benchmark"),
				header: t("SCENARIO_ANALYSIS_EDITABLE.TABLES_FIELDS.BENCHMARK"),
				width: 144,
			},
			{
				name: "graph",
				width: 200,
				header: (headerProps) => {
					const metrics = [
						`${formatNumber(derivedPerformanceGraphLimits?.min, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}%`,
						"0",
						`${formatNumber(derivedPerformanceGraphLimits?.max, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}%`,
					];
					return (
						<Row {...headerProps} alignItems="center" justifyContent="space-between">
							<ForEach collection={metrics}>
								{(item) => <p className="text-[10px] text-[#667085] font-normal">{item}</p>}
							</ForEach>
						</Row>
					);
				},
				content: ({ portfolio, benchmark }, cellProps) => {
					return (
						<Row
							justifyContent="center"
							{...cellProps}
							style={{ ...cellProps.style, height: defaultRowHeight }}
							className={overrideClassList(cellProps.className, "py-2")}
						>
							<BarGraph
								bars={[portfolio ?? 0, benchmark ?? 0]}
								scale={{
									min: derivedPerformanceGraphLimits?.min ?? 0,
									max: derivedPerformanceGraphLimits?.max ?? 0,
								}}
							/>
						</Row>
					);
				},
				hidden: !showContributionGraph,
				footer: (footerProps) => {
					const metrics = [
						`${formatNumber(derivedPerformanceGraphLimits?.min, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}%`,
						"0",
						`${formatNumber(derivedPerformanceGraphLimits?.max, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}%`,
					];
					return (
						<Row {...footerProps} alignItems="center" justifyContent="space-between">
							<ForEach collection={metrics}>
								{(item) => <p className="text-[10px] text-[#667085] font-normal">{item}</p>}
							</ForEach>
						</Row>
					);
				},
			},
		],
		[derivedPerformanceGraphLimits?.max, derivedPerformanceGraphLimits?.min, formatNumber, showContributionGraph, t],
	);

	const portfolioPerformanceContributionColumn = useMemo<
		TableColumn<portfolioPerformanceContributionColumnProps>[]
	>(() => {
		return [
			{
				...commonColumns.string("assetClass"),
				header: t("SCENARIO_ANALYSIS_EDITABLE.TABLES_FIELDS.ASSET_CLASS"),
				minWidth: 144,
				hiderOrderableIcon: false,
			},
			{
				...commonColumns.percentage("portfolioExposure"),
				header: t("SCENARIO_ANALYSIS_EDITABLE.TABLES_FIELDS.PORTFOLIO_EXPOSURE"),
				width: 144,
				hiderOrderableIcon: false,
			},
			{
				...commonColumns.percentage("portfolioReturns"),
				header: t("SCENARIO_ANALYSIS_EDITABLE.TABLES_FIELDS.PORTFOLIO_RETURNS"),
				width: 144,
				hiderOrderableIcon: false,
			},
			{
				...commonColumns.percentage("benchmarkReturns"),
				header: t("SCENARIO_ANALYSIS_EDITABLE.TABLES_FIELDS.BENCHMARK_RETURNS"),
				width: 144,
				hiderOrderableIcon: false,
			},
			{
				name: "graph",
				width: 200,
				header: (headerProps) => {
					const metrics = [
						`${formatNumber(derivedContributionGraphLimits?.min, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}%`,
						"0",
						`${formatNumber(derivedContributionGraphLimits?.max, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}%`,
					];
					return (
						<Row {...headerProps} alignItems="center" justifyContent="space-between">
							<ForEach collection={metrics}>
								{(item) => <p className="text-[10px] text-[#667085] font-normal">{item}</p>}
							</ForEach>
						</Row>
					);
				},
				content: ({ portfolioReturns, benchmarkReturns }, cellProps) => {
					return (
						<Row
							justifyContent="center"
							{...cellProps}
							style={{ ...cellProps.style, height: defaultRowHeight }}
							className={overrideClassList(cellProps.className, "py-2")}
						>
							<BarGraph
								bars={[portfolioReturns, benchmarkReturns]}
								scale={{
									min: derivedContributionGraphLimits?.min ?? 0,
									max: derivedContributionGraphLimits?.max ?? 0,
								}}
							/>
						</Row>
					);
				},
				cellClassName: showContributionGraph ? "mx-auto grow" : "",
				hideColumn: !showContributionGraph,
				footer: (footerProps) => {
					const metrics = [
						`${formatNumber(derivedContributionGraphLimits?.min, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}%`,
						"0",
						`${formatNumber(derivedContributionGraphLimits?.max, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}%`,
					];
					return (
						<Row {...footerProps} alignItems="center" justifyContent="space-between">
							<ForEach collection={metrics}>
								{(item) => <p className="text-[10px] text-[#667085] font-normal">{item}</p>}
							</ForEach>
						</Row>
					);
				},
			},
		];
	}, [
		t,
		showContributionGraph,
		formatNumber,
		derivedContributionGraphLimits?.min,
		derivedContributionGraphLimits?.max,
	]);

	return (
		<div className="grid" style={{ gridTemplateColumns: "805px 1fr" }}>
			<div className="py-4 pr-4 overflow-hidden border-r-2 border-[#EFF0F3]">
				{/* Market Scenario */}
				<div className="flex justify-between w-full mb-4">
					<Text type="Title/S">{t("SCENARIO_ANALYSIS_EDITABLE.MARKET_SCENARIO.MARKET_SCENARIO_TITLE")}</Text>
				</div>
				<div className="mb-4">
					<p>{t("SCENARIO_ANALYSIS_EDITABLE.MARKET_SCENARIO.MARKET_SCENARIO_DESCRIPTION")}</p>
				</div>
				<MarketViewProbabilities
					className="px-0!"
					mode={selectedScenario?.editable ? "edit" : "view"}
					forceSelectMode="edit"
					marketScenarioProvider={async (scenarioId) => {
						const loadedScenario = await axiosExtract(scenarioApi.loadScenario(scenarioId, uuid));
						setOriginalScenario(loadedScenario);
						return loadedScenario;
					}}
					marketScenario={marketScenario}
					onMarketScenarioChange={setMarketScenario}
					scenarioOptions={scenarioOptions}
					selectedScenarioId={currentScenarioId}
					onSelectedScenarioChange={setCurrentScenarioId}
					highlightedAssetClass={highlightedAssetClass}
					alias={marketViewAssetClassAliases?.assetClassAliases}
					hideCommentary
					hidePositioning
				/>
			</div>
			<div className="py-4 pl-4 min-w-0">
				<div className="mb-4 flex items-center gap-2">
					<Text type="Title/S">Expected portfolio performance</Text>
					{performanceQuery.isFetching && (
						<CircularProgressBar value="indeterminate" outerDiameter={20} innerDiameter={12} />
					)}
				</div>
				<div className="mb-12">
					{portfolioPerformanceRow && (
						<AutoSortHScrollTable
							disableVirtualScroll
							columns={portfolioPerformanceColumn}
							rows={portfolioPerformanceRow}
						/>
					)}
				</div>
				<div className="mb-12">
					<div className="mb-4 flex items-center gap-2">
						<Text type="Title/S"> Expected performance contribution by asset class</Text>
						{performanceQuery.isFetching && (
							<CircularProgressBar value="indeterminate" outerDiameter={20} innerDiameter={12} />
						)}
					</div>
					<div ref={setSize} className="relative">
						{portfolioContributionPerformanceRow && (
							<AutoSortHScrollTable
								disableVirtualScroll
								columns={portfolioPerformanceContributionColumn}
								rows={portfolioContributionPerformanceRow}
								orderBy={defaultPortfolioPerformanceContributionOrderBy}
							/>
						)}

						<div className="flex gap-4 py-4">
							{EXPECTED_PTF_PERFORMANCE_CONTRIBUTION_LEGENDS.map(({ name, color }) => (
								<div key={color} className="flex items-center gap-1">
									<ColoredRectangle variant="vertical" color={color} />
									{name}
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
			<DataDisplayOverlay dataSource="Edit MarketView" data={marketScenario} />
			<DataDisplayOverlay
				data={(() => {
					if (!marketScenario || !originalScenario) {
						return [];
					}
					return removeDeltaFromSelectedMarketView(
						marketScenario.flexibleExpectedReturnsVolatility!.assetClasses!,
						originalScenario.flexibleExpectedReturnsVolatility!.assetClasses!,
						marketScenario.regimeUserProbability!,
					);
				})()}
				dataSource="ReConverted MarketView"
			/>
		</div>
	);
});

const defaultPortfolioPerformanceContributionOrderBy: OrderBy[] = [
	{ columnName: "portfolioExposure", direction: "desc" },
];

const ScenarioAnalysisEditable = ({
	portfolio,
	enhanced,
	selectedBenchmark,
}: ContextContent<typeof PortfolioContext>) => {
	const { uuid } = portfolio ?? {};
	const user = useUserValue();
	const defaultScenarioId = useMemo(
		() =>
			hasAccess(user, {
				requiredService: "HIDE_SCENARIO_ANALYSIS_SPHERE_VIEWS",
			})
				? "LEHMAN_CRASH"
				: "SPHERE_FORECAST_ONE_MONTH",
		[user],
	);

	const queryInvestmentExposureDecomposition = portfolioRepository.exposureDecomposition.useQuery(
		uuid,
		selectedBenchmark!,
		enhanced,
	);

	const queryAssetClassAliases = marketViewRepository.assetClassAliases.useQuery();
	const queryAnalysisScenarioList = marketViewRepository.analysisScenarioList.useQuery(uuid, enhanced);

	const queryAnalysisScenario = marketViewRepository.analysisScenario.useQuery(defaultScenarioId, uuid, enhanced);
	const queryMappedDefaultScenario = useMappedQuery(mapScenarioWithDelta, [], queryAnalysisScenario);

	return (
		<>
			{queryMappedDefaultScenario.isLoading ||
			queryInvestmentExposureDecomposition.isLoading ||
			queryAssetClassAliases.isLoading ||
			queryAnalysisScenarioList.isLoading ? (
				<ProgressBar value="indeterminate" />
			) : queryMappedDefaultScenario.data &&
				queryInvestmentExposureDecomposition.data &&
				queryAnalysisScenarioList.data ? (
				<ScenarioAnalysisEditableContentMemo
					performanceContributions={queryInvestmentExposureDecomposition.data}
					sphereScenario={queryMappedDefaultScenario.data}
					summary={portfolio}
					benchmarkId={selectedBenchmark}
					enhanced={enhanced}
					defaultScenarioId={defaultScenarioId}
					marketViewAssetClassAliases={queryAssetClassAliases.data}
					scenarioAnalysisList={queryAnalysisScenarioList.data.scenarios ?? []}
				/>
			) : (
				<div className="h-[720px] bg-white w-full">
					<IconWalls.ErrorData />
				</div>
			)}
		</>
	);
};

export default withContext(PortfolioContext)(ScenarioAnalysisEditable);
